import { type JSX, type PropsWithChildren, createElement, forwardRef } from 'react';

import {
  breakpointVariants,
  gridAreaVars,
  gridColumnStartVars,
  gridColumnEndVars,
  gridColumnVars,
  gridRowStartVars,
  gridRowEndVars,
  gridRowVars,
  orderVars,
} from './GridItem.css';
import { DynamicVariantProp, MediaQueryKeys } from '../../types';
import { getClassNames } from '../../util/css';
import { extractDynamicStyles } from '../../util/css/vanilla-extract';

const sparkyClassName = 'sparky-grid-item';

export const GridItem = forwardRef<HTMLDivElement, PropsWithChildren<GridItemProps>>(
  (
    {
      children,
      as = 'div',
      className = '',
      gridArea,
      gridColumnStart,
      gridColumnEnd,
      gridColumn,
      gridRowStart,
      gridRowEnd,
      gridRow,
      order,
    },
    ref,
  ) => {
    const { classNames: dynamicCssClasses, cssVars } = extractDynamicStyles({
      gridArea: [breakpointVariants.gridArea, gridAreaVars, gridArea],
      gridColumnStart: [breakpointVariants.gridColumnStart, gridColumnStartVars, gridColumnStart],
      gridColumnEnd: [breakpointVariants.gridColumnEnd, gridColumnEndVars, gridColumnEnd],
      gridColumn: [breakpointVariants.gridColumn, gridColumnVars, gridColumn],
      gridRowStart: [breakpointVariants.gridRowStart, gridRowStartVars, gridRowStart],
      gridRowEnd: [breakpointVariants.gridRowEnd, gridRowEndVars, gridRowEnd],
      gridRow: [breakpointVariants.gridRow, gridRowVars, gridRow],
      order: [breakpointVariants.order, orderVars, order],
    });

    const classNames = getClassNames(sparkyClassName, ...dynamicCssClasses, className);

    return createElement(as, { className: classNames, style: cssVars, ref }, children);
  },
);

GridItem.toString = () => `.${sparkyClassName}`;
GridItem.displayName = 'Grid.Item';

type GridItemProps = {
  /** This is the [native grid-area CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-area) that will be directly passed on to the grid item.   */
  gridArea?: DynamicVariantProp<'gridArea'>;

  /** This is the [native grid-column-start CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column-start) that will be directly passed on to the grid item and correlates to the `PageGrid` column count. */
  gridColumnStart?: GridColumnSingleOrResponsive;

  /** This is the [native grid-column-end CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column-end) that will be directly passed on to the grid item and correlates to the `PageGrid` column count. */
  gridColumnEnd?: GridColumnSingleOrResponsive;

  /** This is the [native grid-column CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column) that will be directly passed on to the grid item and correlates to the `PageGrid` column count. */
  gridColumn?: GridColumnShortHandSingleOrResponsive;

  /** This is the [native grid-row-start CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row-start) that will be directly passed on to the grid item.   */
  gridRowStart?: DynamicVariantProp<'gridRowStart'>;

  /** This is the [native grid-row-end CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row-end) that will be directly passed on to the grid item.   */
  gridRowEnd?: DynamicVariantProp<'gridRowEnd'>;

  /** This is the [native grid-row CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row) that will be directly passed on to the grid item.   */
  gridRow?: DynamicVariantProp<'gridRow'>;

  /** This is the [native order CSS property](https://developer.mozilla.org/en-US/docs/Web/CSS/order) that will be directly passed on to the grid item.   */
  order?: DynamicVariantProp<'order'>;

  /** The DOM element to render */
  as?: keyof JSX.IntrinsicElements;
  className?: never;
};

type ColumnsMobile = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type ColumnsDesktop = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12' | '13';

type ColumnsAllowed<Cols extends string> =
  | Cols
  | `-${Cols}`
  | `${Cols | `-${Cols}`}/${Cols | `-${Cols}`}`
  | `${Cols | `-${Cols}`}/span ${Cols | `-${Cols}`}`;

type MediaQueryGridColumn<M extends string, D extends string> = {
  [key in MediaQueryKeys]?: key extends 'initial' | 'sm' ? M : D;
};

type GridColumnShortHandSingleOrResponsive =
  | ColumnsAllowed<ColumnsDesktop>
  | MediaQueryGridColumn<ColumnsAllowed<ColumnsMobile>, ColumnsAllowed<ColumnsDesktop>>;

type GridColumnSingleOrResponsive =
  | ColumnsAllowed<ColumnsDesktop>
  | MediaQueryGridColumn<ColumnsAllowed<ColumnsMobile>, ColumnsAllowed<ColumnsDesktop>>;
